import React, { useState, useEffect } from 'react';
import { Users, Mail, TrendingUp, Clock, CheckCircle, Eye, LogOut } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import AdminLogin from './AdminLogin';
import { Button } from './ui/button';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check if user is already authenticated
    const authenticated = sessionStorage.getItem('admin_authenticated') === 'true';
    setIsAuthenticated(authenticated);
    
    if (authenticated) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps 
}, []);
  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    fetchDashboardData();
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_authenticated');
    setIsAuthenticated(false);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsResponse = await fetch(`${BACKEND_URL}/api/admin/dashboard`);
      const statsData = await statsResponse.json();
      setStats(statsData);

      // Fetch consultations
      const consultationsResponse = await fetch(`${BACKEND_URL}/api/consultations`);
      const consultationsData = await consultationsResponse.json();
      setConsultations(consultationsData);

      // Fetch contacts
      const contactsResponse = await fetch(`${BACKEND_URL}/api/contacts`);
      const contactsData = await contactsResponse.json();
      setContacts(contactsData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const updateConsultationStatus = async (id, newStatus) => {
    try {
      await fetch(`${BACKEND_URL}/api/consultations/${id}/status?new_status=${newStatus}`, {
        method: 'PATCH',
      });
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const updateContactStatus = async (id, newStatus) => {
    try {
      await fetch(`${BACKEND_URL}/api/contacts/${id}/status?new_status=${newStatus}`, {
        method: 'PATCH',
      });
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1565C0] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Manage consultations and contact inquiries</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="flex items-center gap-2"
          >
            <LogOut size={18} />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Consultations</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_consultations}</p>
                    <p className="text-xs text-[#1565C0] mt-1">
                      {stats.today_consultations} today
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#1565C0] flex items-center justify-center">
                    <Users size={24} className="text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Pending</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.pending_consultations}</p>
                    <p className="text-xs text-[#FF7043] mt-1">Need attention</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#FF7043] flex items-center justify-center">
                    <Clock size={24} className="text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Contacts</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_contacts}</p>
                    <p className="text-xs text-[#8BC34A] mt-1">
                      {stats.today_contacts} today
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#8BC34A] flex items-center justify-center">
                    <Mail size={24} className="text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">New Messages</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.new_contacts}</p>
                    <p className="text-xs text-purple-600 mt-1">Unread</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center">
                    <TrendingUp size={24} className="text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="consultations" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="consultations" className="px-6 py-3">
              Consultation Bookings ({consultations.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="px-6 py-3">
              Contact Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          {/* Consultations Tab */}
          <TabsContent value="consultations">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Email / Phone</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Country</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consultations.map((consultation) => (
                        <tr key={consultation.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">{consultation.name}</div>
                            <div className="text-xs text-gray-500">{formatDate(consultation.created_at)}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm text-gray-700">{consultation.email}</div>
                            <div className="text-xs text-gray-500">{consultation.phone}</div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{consultation.service || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{consultation.country || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">
                            {consultation.date || '-'} {consultation.time && `(${consultation.time})`}
                          </td>
                          <td className="py-3 px-4">
                            <select
                              value={consultation.status}
                              onChange={(e) => updateConsultationStatus(consultation.id, e.target.value)}
                              className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                consultation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                consultation.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                                consultation.status === 'completed' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => alert(`Message: ${consultation.message || 'No message'}`)}
                              className="text-[#1565C0] hover:text-[#0D47A1]"
                            >
                              <Eye size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {consultations.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No consultation bookings yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Email / Phone</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Subject</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Message</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">{contact.name}</div>
                            <div className="text-xs text-gray-500">{formatDate(contact.created_at)}</div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-sm text-gray-700">{contact.email}</div>
                            <div className="text-xs text-gray-500">{contact.phone || '-'}</div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{contact.subject || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700 max-w-xs truncate">{contact.message}</td>
                          <td className="py-3 px-4">
                            <select
                              value={contact.status}
                              onChange={(e) => updateContactStatus(contact.id, e.target.value)}
                              className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                contact.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                                contact.status === 'read' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}
                            >
                              <option value="new">New</option>
                              <option value="read">Read</option>
                              <option value="responded">Responded</option>
                            </select>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => alert(`Full Message:\n\n${contact.message}`)}
                              className="text-[#1565C0] hover:text-[#0D47A1]"
                            >
                              <Eye size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contacts.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No contact messages yet
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
